import { AxiosError } from 'axios';
import { AutocompleteAdapter } from 'components/enhanced/form/adapters/selects/AutocompleteAdapter';
import { MultipleAutocompleteAdapter } from 'components/enhanced/form/adapters/selects/MultipleAutocompleteAdapter';
import { TextFieldAdapter } from 'components/enhanced/form/adapters/text-inputs/TextFieldAdapter';
import EnhancedForm from 'components/enhanced/form/EnhancedForm';
import { IdentityField } from 'components/enhanced/form/fields/IdentityField';
import { ProductWarehouseStatus } from 'features/inventory/products/constants';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { categoryService, locationService } from 'shared/services';
import toast from 'shared/utils/toast';
import { Option, StatusOption } from 'shared/types/general';
import { labelThresholdService } from '../../api/label-threshold.service';
import { productWarehouseService } from '../../api/product-warehouse.service';
import { EnhancedDialog } from 'components/enhanced';
import { SectionHeader } from 'components/ui/typography';

interface MiscFormParams {
  // initial values
  // eslint-disable-next-line
  row: any;
  open: boolean;
  onClose: () => void;
  // refresher functions
  refresh?: React.RefObject<() => void>;
}

function resolveProductWarehouseStatusOption(
  status: unknown,
  options: StatusOption[]
): StatusOption | undefined {
  if (status === null || status === undefined || status === '') {
    return undefined;
  }
  const str = String(status).trim();
  const num = Number(str);
  const byValue = options.find(
    (o) => o.value === status || o.value === num || String(o.value) === str
  );
  if (byValue) {
    return byValue;
  }
  return options.find(
    (o) => o.display_name === str || (typeof status === 'string' && o.display_name === status)
  );
}

function MiscDialogForm({ row, open, onClose, refresh }: MiscFormParams) {
  const { t } = useTranslation();
  const translationPrefix = 'pages.warehouse.edit.misc_product_warehouse_form';

  const [categories, setCategories] = useState<Option[]>([]);
  const [labelCodes, setLabelCodes] = useState<string[]>([]);
  const [locations, setLocations] = useState<Option[]>([]);

  const allStatusOptions: StatusOption[] = useMemo(
    () =>
      Object.keys(ProductWarehouseStatus)
        .filter((key) => isNaN(Number(key)))
        .map((key) => ({
          value: ProductWarehouseStatus[key as keyof typeof ProductWarehouseStatus],
          display_name: key
        })),
    []
  );

  const rowStatusNum = useMemo(() => {
    return resolveProductWarehouseStatusOption(row.status, allStatusOptions)?.value;
  }, [row.status, allStatusOptions]);

  const stockQty = Number(row.total_stock_quantity ?? 0);

  const statusOptions = useMemo(() => {
    const opts =
      stockQty > 0
        ? allStatusOptions.filter(
            (o) => o.value !== ProductWarehouseStatus.DECOMMISSIONED || o.value === rowStatusNum
          )
        : allStatusOptions;
    return opts.map((o) => ({
      ...o,
      display_name: t(`entities.product.status_options.${o.display_name}`)
    }));
  }, [allStatusOptions, stockQty, rowStatusNum, t]);

  useEffect(() => {
    labelThresholdService.getAllBaseInformation().then((res) => {
      setLabelCodes(res.map((opt) => opt.code));
    });
    categoryService.getAllBaseInformation().then(setCategories);
    locationService.getAllBaseInformation({ warehouse: row.warehouse }).then(setLocations);
  }, [row.warehouse]);

  const categoryCode = useMemo(
    () => (row.category != null && row.category !== '' ? String(row.category).trim() : ''),
    [row.category]
  );

  const categoryInit = useMemo(
    () =>
      categoryCode ? categories.find((c) => String(c.code).trim() === categoryCode) : undefined,
    [categories, categoryCode]
  );

  const statusInit = useMemo(
    () => resolveProductWarehouseStatusOption(row.status, allStatusOptions),
    [row.status, allStatusOptions]
  );

  const labelCode = row.label != null && row.label !== '' ? String(row.label).trim() : '';

  const labelOptions = useMemo(() => {
    const opts = labelCodes.map((code) => ({ code, display_name: code }));
    if (labelCode && !labelCodes.includes(labelCode)) {
      return [{ code: labelCode, display_name: labelCode }, ...opts];
    }
    return opts;
  }, [labelCodes, labelCode]);

  const labelInit = useMemo(() => {
    if (!labelCode) {
      return null;
    }
    return { code: labelCode, display_name: labelCode };
  }, [labelCode]);

  const formInitialValues = useMemo(
    () => ({
      category: categoryCode || '',
      status: rowStatusNum ?? '',
      label: labelCode || null,
      understock_threshold: row.understock_threshold ?? '',
      overstock_threshold: row.overstock_threshold ?? '',
      restock_threshold: row.restock_threshold ?? '',
      protocollo: row.protocollo ?? '',
      notes: row.notes ?? ''
    }),
    [
      row.product,
      row.warehouse,
      categoryCode,
      rowStatusNum,
      labelCode,
      row.understock_threshold,
      row.overstock_threshold,
      row.restock_threshold,
      row.protocollo,
      row.notes
    ]
  );

  const decommissionBlockedMessage = t(`${translationPrefix}.cannot-decommission-with-stock`);

  // eslint-disable-next-line
  const handleSubmit = async (values: any) => {
    const newStatus = values.status;

    const updatedValues = {
      ...values,
      product: row.product,
      warehouse: row.warehouse,
      label:
        values.label === '' || values.label === undefined || values.label === null
          ? null
          : values.label,
      category:
        values.category === '' || values.category === undefined || values.category === null
          ? null
          : values.category
    };

    try {
      await productWarehouseService.updateThresholds([updatedValues]);
      if (refresh && refresh.current) {
        refresh.current();
        toast.success(t('actions.successfully.updated'));
      }
      onClose();
    } catch (error: unknown) {
      if (newStatus === ProductWarehouseStatus.DECOMMISSIONED && error instanceof AxiosError) {
        const serverError = (error.response?.data as { error?: string } | undefined)?.error;
        toast.error(serverError || decommissionBlockedMessage);
      }
    }
  };

  return (
    <EnhancedDialog
      open={open}
      onClose={onClose}
      title={t('actions.edit')}
      subTitle={t(`${translationPrefix}.edit-threshold-title`, {
        product: row.product,
        warehouse: row.warehouse
      })}
      submitLabel={t('actions.save')}
      cancelLabel={t('actions.cancel')}
      maxWidth="md"
      submitButtonProps={{
        type: 'submit',
        form: 'updateProductWarehouseThresholdsAndLocationForm'
      }}>
      <EnhancedForm
        onSubmit={handleSubmit}
        hideControls
        initialValues={formInitialValues}
        formId="updateProductWarehouseThresholdsAndLocationForm"
        autoStyleLayout
        columns={4}>
        <SectionHeader
          title={t(`${translationPrefix}.thresholds-edit-section`)}
          subtitle={t(`${translationPrefix}.thresholds-edit-section-subtitle`)}
          gridSpan
        />

        <IdentityField
          gridSpan={2}
          name="category"
          label={t('global.category')}
          component={AutocompleteAdapter}
          initValue={categoryInit}
          options={categories}
          optionValue="code"
          placeholder="Category"
        />

        {statusOptions && (
          <IdentityField
            gridSpan={2}
            name="status"
            label={t(`entities.product.status`)}
            component={AutocompleteAdapter}
            initValue={statusInit}
            options={statusOptions}
            placeholder="Status"
            type="text"
            optionLabel="display_name"
            optionValue="value"
          />
        )}

        <IdentityField
          name="label"
          component={AutocompleteAdapter}
          options={labelOptions}
          initValue={labelInit}
          label={t('global.label')}
          optionValue="code"
          optionLabel="display_name"
          freeSolo={true}
          // disablePortal
        />

        <IdentityField
          name="understock_threshold"
          component={TextFieldAdapter}
          label={t('entities.product.understock_threshold')}
          type="number"
        />

        <IdentityField
          name="overstock_threshold"
          component={TextFieldAdapter}
          label={t('entities.product.overstock_threshold')}
          type="number"
        />

        <IdentityField
          name="restock_threshold"
          component={TextFieldAdapter}
          label={t('entities.product.restock_threshold')}
          type="number"
        />

        <IdentityField
          gridSpan={4}
          name="protocollo"
          component={TextFieldAdapter}
          label={t('entities.product.protocollo')}
          type="text"
        />

        <IdentityField
          gridSpan={4}
          name="notes"
          component={TextFieldAdapter}
          label={t('global.notes')}
          type="text"
        />

        <SectionHeader
          title={t(`${translationPrefix}.locations-edit-section`)}
          subtitle={t(`${translationPrefix}.locations-edit-section-subtitle`)}
          gridSpan
        />
        {locations.length > 0 && (
          <IdentityField
            gridSpan={4}
            name="locations"
            component={MultipleAutocompleteAdapter}
            options={locations}
            initValue={row.location?.map((locCode: string) =>
              locations.find((s) => s.code === locCode)
            )}
            label={t('global.locations')}
            type="text"
          />
        )}
      </EnhancedForm>
    </EnhancedDialog>
  );
}

export default MiscDialogForm;
